import React from "react";
import { STATUS_STYLE, PRIORITY_STYLE, HEALTH_STYLE } from "../../lib/api";

export const Avatar = ({ person, size = 32, className = "" }) => {
  const ring = className.includes("ring-0") ? "" : "ring-2 ring-white";
  if (!person)
    return (
      <div
        className={`flex shrink-0 items-center justify-center rounded-full bg-slate-200 font-semibold text-slate-500 ${ring} ${className}`}
        style={{ width: size, height: size, fontSize: size * 0.36 }}
      >
        –
      </div>
    );
  return (
    <div
      title={person.name}
      className={`flex shrink-0 items-center justify-center rounded-full font-bold text-white ${ring} ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.34, backgroundColor: person.color }}
    >
      {person.initials}
    </div>
  );
};

export const StatusPill = ({ status, className = "" }) => {
  const s = STATUS_STYLE[status] || STATUS_STYLE["Not Started"];
  return (
    <span
      data-testid="status-pill"
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${className}`}
      style={{ backgroundColor: s.bg, color: s.fg }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: s.dot }} />
      {status}
    </span>
  );
};

export const PriorityPill = ({ priority }) => {
  const s = PRIORITY_STYLE[priority] || PRIORITY_STYLE.Medium;
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: s.bg, color: s.fg }}>
      {priority}
    </span>
  );
};

export const HealthPill = ({ health }) => {
  const s = HEALTH_STYLE[health] || HEALTH_STYLE["On Track"];
  return (
    <span className="inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold" style={{ backgroundColor: s.bg, color: s.fg }}>
      {health}
    </span>
  );
};

export const Progress = ({ value, className = "" }) => (
  <div className={`h-2 w-full overflow-hidden rounded-full bg-slate-100 ${className}`}>
    <div className="h-full rounded-full bg-[#00573f] transition-[width] duration-700" style={{ width: `${value || 0}%` }} />
  </div>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
    <div>
      <h2 className="text-2xl font-bold tracking-tight text-slate-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${className}`} {...rest}>
    {children}
  </div>
);

export const Segmented = ({ options, value, onChange, testid }) => (
  <div className="inline-flex max-w-full overflow-x-auto rounded-lg bg-slate-100 p-1" data-testid={testid}>
    {options.map((o) => {
      const Icon = o.icon;
      const active = value === o.value;
      return (
        <button
          key={o.value}
          data-testid={`${testid}-${o.value}`}
          onClick={() => onChange(o.value)}
          className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
            active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
          }`}
        >
          {Icon && <Icon className="h-3.5 w-3.5" />}
          {o.label}
        </button>
      );
    })}
  </div>
);

export const HarrodsMark = ({ className = "" }) => (
  <span className={`font-harrods text-xl font-bold italic leading-none tracking-tight text-[#00573f] ${className}`}>Harrods</span>
);

// SVG donut
export const Donut = ({ segments, size = 140, stroke = 22 }) => {
  const total = segments.reduce((a, s) => a + s.value, 0) || 1;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
      {segments.map((s) => {
        const len = (s.value / total) * c;
        const el = (
          <circle
            key={s.label}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-offset}
            className="transition-[stroke-dasharray] duration-700"
          />
        );
        offset += len;
        return el;
      })}
    </svg>
  );
};
